import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  ViewEncapsulation,
  WritableSignal,
  inject,
  output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormFieldComponent,
  FormExtendedCheckboxComponent,
  FormFieldHeaderComponent,
  ButtonComponent,
  FormMultipleFieldComponent,
  MessageComponent,
  InputComponent,
  FormField,
} from '@akostm/orbit-ui';
import { faPlus, faMinus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FormControl, FormGroup, FormArray, ReactiveFormsModule, Validators } from '@angular/forms';
import { ConversationFormDialogResult } from '../../../interfaces';
import { ConversationType } from '@clientwire/api';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { oneOfFieldsRequiredValidator, emailRegexpValidator } from '../../../validators';
import { ErrorState } from 'libs/shared/src/stores/error.store';
import { PhoneNumberUtil } from 'google-libphonenumber';

@Component({
  selector: 'shared-create-conversation-form',
  standalone: true,
  imports: [
    CommonModule,
    FormFieldComponent,
    FormExtendedCheckboxComponent,
    FormFieldHeaderComponent,
    ButtonComponent,
    ReactiveFormsModule,
    FormMultipleFieldComponent,
    MessageComponent,
    InputComponent,
    TranslatePipe,
  ],
  templateUrl: './create-conversation-form.component.html',
  styleUrl: './create-conversation-form.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateConversationFormComponent implements OnInit {
  dialogButtonClicked = output<ConversationFormDialogResult>();
  favoriteConversationTypeSelected = output<ConversationType | undefined>();
  conversationTypes: ConversationType[] | undefined;
  dataExpanded = false;
  error: WritableSignal<ErrorState[]> | undefined;
  data: {
    generatedID: string;
    conversationTypes: ConversationType[];
    error: WritableSignal<ErrorState[]> | undefined;
    preselectedConversationType: string | undefined;
  } = inject(DIALOG_DATA);
  readonly #translateService = inject(TranslateService);
  readonly #destroyRef = inject(DestroyRef);

  preselectedConversationTypeId: string | undefined;
  faTrash = faTrash;
  faPlus = faPlus;
  conversationTypeForm = new FormGroup({
    conversationID: new FormControl<string>('', [Validators.required]),
    conversationDescription: new FormControl<string>(''),
    conversationType: new FormControl<string>('', [Validators.required]),
    conversationData: new FormControl<string>(''),
    participants: new FormArray([this.createParticipantFormGroup()]),
  });
  readonly formSubmitting = signal(false);

  constructor() {
    this.conversationType.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.conversationTypes?.find((type) => {
        if (type.id === value) {
          this.#setConversationData(type.conversationDataSchema as any);
        }
      });
    });
  }

  ngOnInit(): void {
    if (this.data.generatedID) {
      this.conversationID.setValue(this.data.generatedID);
    }
    this.conversationTypes = this.data.conversationTypes;

    this.preselectedConversationTypeId = this.data.preselectedConversationType ?? '';
    // preselect conversation type if set
    this.conversationTypeForm.get('conversationType')?.setValue(this.preselectedConversationTypeId);

    this.error = this.data.error;
  }

  createParticipantFormGroup(index?: number) {
    const clientID = index !== undefined && index > -1 ? `Client${index}` : 'Client';
    const formGroup = new FormGroup(
      {
        name: new FormControl<string>(''),
        id: new FormControl<string>(clientID, [Validators.required]),
        phone: new FormControl<string>(''),
        email: new FormControl<string>('', [emailRegexpValidator()]),
      },
      { validators: oneOfFieldsRequiredValidator(['phone', 'email']) }
    );

    const phoneField = formGroup.get('phone') as FormControl<string>;
    const nameField = formGroup.get('name') as FormControl<string>;
    const emailField = formGroup.get('email') as FormControl<string>;

    phoneField?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
      let formattedNumber = value;
      if (formattedNumber) {
        const phoneUtil = PhoneNumberUtil.getInstance();
        try {
          const number = phoneUtil.parse(value, 'AT');
          formattedNumber = phoneUtil.format(number, 0);
          phoneField.setValue(formattedNumber, { emitEvent: false });
        } catch (e) {
          // If parsing fails, do nothing
        }
      }

      if (nameField.pristine && value && !emailField.value) {
        nameField.setValue(formattedNumber);
      }
    });

    emailField?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((value) => {
      if (nameField.pristine && value) {
        nameField.setValue(emailField.value);
      }
    });

    return formGroup;
  }

  get participants() {
    return this.conversationTypeForm.get('participants') as FormArray;
  }

  addParticipant() {
    const newParticipant = this.createParticipantFormGroup(this.participants.length + 1);
    this.participants.push(newParticipant);
  }

  removeParticipant(index: number) {
    if (this.participants.length > 1) {
      this.participants.removeAt(index);
    }
  }

  participantFields(index: number): FormField[] {
    return [
      {
        label: this.#translateService.instant('SHARED.CREATE_CONVERSATION_FORM.NAME_LABEL'),
        name: 'conversation-participant-name',
        type: 'text',
        controller: this.conversationParticipants.at(index).get('name') as FormControl<string>,
      },
      {
        label: this.#translateService.instant(
          'SHARED.CREATE_CONVERSATION_FORM.PARTICIPANT_ID_LABEL'
        ),
        name: 'conversation-participant-id',
        type: 'text',
        controller: this.conversationParticipants.at(index).get('id') as FormControl<string>,
      },
      {
        label: this.#translateService.instant('SHARED.CREATE_CONVERSATION_FORM.PHONE_LABEL'),
        name: 'conversation-participant-phone',
        type: 'text',
        controller: this.conversationParticipants.at(index).get('phone') as FormControl<string>,
      },
      {
        label: this.#translateService.instant('SHARED.CREATE_CONVERSATION_FORM.EMAIL_LABEL'),
        name: 'conversation-participant-email',
        type: 'email',
        controller: this.conversationParticipants.at(index).get('email') as FormControl<string>,
      },
    ];
  }

  getDataIcon() {
    if (this.dataExpanded) {
      return faMinus;
    }
    return faPlus;
  }

  handleExpand() {
    this.dataExpanded = !this.dataExpanded;
  }

  get conversationID() {
    return this.conversationTypeForm.get('conversationID') as FormControl<string>;
  }
  get conversationDescription() {
    return this.conversationTypeForm.get('conversationDescription') as FormControl<string>;
  }
  get conversationType() {
    return this.conversationTypeForm.get('conversationType') as FormControl<string>;
  }
  get conversationData() {
    return this.conversationTypeForm.get('conversationData') as FormControl<string>;
  }
  get conversationParticipants() {
    return this.conversationTypeForm.get('participants') as FormArray<FormGroup>;
  }

  conversationParticipant(index: number) {
    return {
      name: this.conversationParticipants.at(index).get('name')?.value,
      id: this.conversationParticipants.at(index).get('id')?.value,
      phone: this.conversationParticipants.at(index).get('phone')?.value,
      email: this.conversationParticipants.at(index).get('email')?.value,
    };
  }

  onSubmit(): void {
    if (
      !this.conversationID.value ||
      !this.conversationType.value ||
      !this.conversationParticipants.value.every((participant) => participant.id)
    ) {
      return;
    }
    this.formSubmitting.set(true);

    // Set the name to the email or phone if it is empty
    const participants = this.participants.value;
    participants.forEach((participant: any) => {
      if (participant.name === '') {
        if (participant.email) {
          participant.name = participant.email;
        } else {
          participant.name = participant.phone;
        }
      }
    });

    this.dialogButtonClicked.emit({
      eventType: 'SAVE',
      data: {
        id: this.conversationID.value,
        participants: participants,
        type: this.conversationType.value,
        data: this.conversationData.value,
        description: this.conversationDescription.value,
      },
    });
  }

  #setConversationData(schema: { [key: string]: any }) {
    const value: { [key: string]: any } = {};
    if (schema?.['required'] && Array.isArray(schema['required'])) {
      schema['required'].forEach((key: string) => {
        value[key] = '';
      });
    }
    this.conversationData.setValue(JSON.stringify(value));
  }

  onConversationTypeFavoriteClicked(id: string) {
    // user clicked on an already favorite type => un-favorite
    if (id == this.preselectedConversationTypeId) {
      id = '';
    }

    const preselectedConversationType = this.conversationTypes?.find((it) => id == it?.id);
    this.preselectedConversationTypeId = preselectedConversationType?.id;
    this.favoriteConversationTypeSelected.emit(preselectedConversationType);
  }
}
